import { z } from 'zod';
import { AspectRatio, SceneType } from '../enums';
import { scriptSegmentSchema, graphicsSpecSchema } from './director';
import type { Notice } from './common';

/**
 * Проект и сцены (§17-21).
 */

export const ProjectStatus = {
  DRAFT: 'draft',
  DIRECTING: 'directing',
  STORYBOARD: 'storyboard',
  GENERATING: 'generating',
  PARTIAL: 'partial',
  READY: 'ready',
  FAILED: 'failed',
} as const;
export type ProjectStatus = (typeof ProjectStatus)[keyof typeof ProjectStatus];

export const projectStatusLabels: Record<ProjectStatus, string> = {
  draft: 'Черновик',
  directing: 'Собираем раскадровку',
  storyboard: 'Раскадровка готова',
  generating: 'Создаём',
  partial: 'Готово частично',
  ready: 'Готово',
  failed: 'Не удалось',
};

export const SceneStatus = {
  DRAFT: 'draft',
  QUEUED: 'queued',
  GENERATING: 'generating',
  POSTPROCESSING: 'postprocessing',
  READY: 'ready',
  FAILED: 'failed',
  NEEDS_INPUT: 'needs_input',
} as const;
export type SceneStatus = (typeof SceneStatus)[keyof typeof SceneStatus];

export const sceneStatusLabels: Record<SceneStatus, string> = {
  draft: 'Не создана',
  queued: 'В очереди',
  generating: 'Создаём',
  postprocessing: 'Финальная обработка',
  ready: 'Готова',
  failed: 'Не удалось',
  needs_input: 'Нужен материал',
};

/** §15: дополнительные параметры видео. */
export const createProjectSchema = z
  .object({
    prompt: z.string().trim().min(10, 'Опишите, что нужно создать').max(4000),
    aspectRatio: z.enum(Object.values(AspectRatio) as [string, ...string[]]).default('9:16'),
    /** null = Auto: директор сам решает по содержанию. */
    durationSec: z.number().int().min(5).max(600).nullable().default(null),
    lookId: z.string().min(1).nullable().default(null),
    captionsEnabled: z.boolean().default(true),
    language: z.string().min(2).max(8).default('ru'),
    /** §15: дополнительные материалы — фото, видео, документы. */
    materialAssetIds: z.array(z.string().min(1)).max(10).default([]),
  })
  .strict();
export type CreateProjectInput = z.infer<typeof createProjectSchema>;

/** §21: действия со сценой. */
export const updateSceneSchema = z
  .object({
    text: z.string().trim().max(2000).optional(),
    script: z.array(scriptSegmentSchema).max(8).optional(),
    visualPrompt: z.string().trim().max(500).optional(),
    durationSec: z.number().min(1).max(30).optional(),
    lookId: z.string().min(1).nullable().optional(),
    type: z.enum(Object.values(SceneType) as [SceneType, ...SceneType[]]).optional(),
    sourceAssetIds: z.array(z.string().min(1)).max(5).optional(),
    graphics: graphicsSpecSchema.nullable().optional(),
  })
  .strict();

export const insertSceneSchema = z
  .object({
    /** Вставить до или после указанной сцены (§21). */
    afterSceneId: z.string().min(1).nullable().default(null),
    type: z.enum(Object.values(SceneType) as [SceneType, ...SceneType[]]).default('broll'),
  })
  .strict();

export const moveSceneSchema = z
  .object({
    afterSceneId: z.string().min(1).nullable(),
  })
  .strict();

export interface SceneVariantView {
  id: string;
  url: string;
  posterUrl: string | null;
  isSelected: boolean;
  createdAt: string;
}

export interface SceneView {
  id: string;
  order: number;
  type: SceneType;
  typeLabel: string;
  durationSec: number;
  text: string | null;
  visualPrompt: string | null;
  lookId: string | null;
  status: SceneStatus;
  statusLabel: string;
  /** Три этапа §68 либо null. */
  stageLabel: string | null;
  errorCode: string | null;
  /** §22: чего не хватает — кодом, текст подставляет клиент. */
  missingRequirements: Notice[];
  estimatedUnits: number;
  spentUnits: number;
  previewUrl: string | null;
  /**
   * Готовый клип сцены — его и смотрят.
   *
   * Отдельно от `previewUrl`: там кадр-обложка, здесь сам файл. Пока
   * ссылки не было, посмотреть результат сцены было негде: карточка
   * показывала картинку, панель — редактор, а видео не показывал никто.
   */
  videoUrl: string | null;
  /** Варианты сцены: их сравнивают глазами и выбирают (§24). */
  variants: SceneVariantView[];
  graphics: unknown;
  /** §28: титры сцены, если уже созданы. */
  captions: unknown;
  variantCount: number;
}

export interface ProjectView {
  id: string;
  title: string;
  originalPrompt: string;
  goal: string | null;
  status: ProjectStatus;
  statusLabel: string;
  aspectRatio: string;
  durationSec: number;
  language: string;
  captionsEnabled: boolean;
  lookId: string | null;
  estimatedUnits: number;
  spentUnits: number;
  errorCode: string | null;
  scenes: SceneView[];
  createdAt: string;
  updatedAt: string;
}

/** Экран расчёта §36. */
export interface ProjectEstimate {
  sceneCount: number;
  totalDurationSec: number;
  totalUnits: number;
  available: number;
  enough: boolean;
  missing: number;
  lines: Array<{ operation: string; title: string; quantity: number; units: number }>;
}

/**
 * Из чего собран ролик (§20).
 *
 * Продуктовые вещи, а не внутренние: §41 запрещает называть поставщиков
 * в пользовательской части, и человека интересует не это, а что пошло
 * в кадр — его голос, выбранный образ, вид графики.
 */
export interface SceneBreakdownView {
  order: number;
  type: SceneType;
  typeLabel: string;
  durationSec: number;
  hasVoice: boolean;
  lookName: string | null;
  graphicsLabel: string | null;
  hasCaptions: boolean;
  spentUnits: number;
}

export interface ProjectBreakdownView {
  aspectRatio: string;
  language: string;
  musicEnabled: boolean;
  captionsEnabled: boolean;
  durationSec: number;
  spentUnits: number;
  scenes: SceneBreakdownView[];
}

/**
 * Коридор первого входа (§6, §71).
 *
 * Пока первый ролик не получен, человек идёт по одному шагу за раз, а не
 * попадает в кабинет с восемью разделами. Шаг выводится из состояния,
 * а не считается: коридор переживает возврат через сутки.
 */
export type OnboardingStep =
  | 'capture'
  | 'checking'
  | 'recapture'
  | 'avatar'
  | 'consent'
  | 'voice'
  | 'look'
  | 'project'
  | 'render'
  | 'done';

export interface OnboardingView {
  step: OnboardingStep;
  /** Коридор пройден — кабинет открыт. */
  completed: boolean;
  /** Шаг можно пропустить: так устроен только образ. */
  canSkip: boolean;
  /** Проект первого ролика, когда он уже создан. */
  projectId: string | null;
}

/**
 * Чем сделана сцена (§41, §51).
 *
 * Только для админа: §41 запрещает имена внешних сервисов в пользовательской
 * части, и поэтому это отдельный тип, а не поле `SceneView`.
 */
export interface SceneTraceStep {
  kind: string;
  provider: string;
  model: string | null;
  status: string;
  /** §35: себестоимость наружу не выходит — только в админской части. */
  costCents: number;
}

export interface SceneTraceRow {
  sceneId: string;
  generatedAt: string | null;
  steps: SceneTraceStep[];
}
